import Decimal from "decimal.js";
import { z } from "zod";

export function getMessageOrDefault(item: unknown, defaultMessage: string): string {
  if (
    typeof item === 'object' &&
    item != null &&
    !Array.isArray(item) &&
    'message' in item &&
    typeof item.message === 'string' &&
    item.message
  ) {
    return item.message;
  }

  return defaultMessage;
}

export function getPath(obj: Record<string, any>, path: string[], defaultValue?: any) {
  const result = path.reduce((acc, key) => {
    if (acc && key in acc) {
      return acc[key];
    }
    return undefined;
  }, obj);

  if (result == null) {
    return result || defaultValue;
  }

  return result;
}

export function sortByArrayLength(key: string) {
  return function sortByArrayLengthHelper(a: any, b: any) {
    const aItem = getPath(a, key.split("."));
    const bItem = getPath(b, key.split("."));
    const aLength = Array.isArray(aItem) ? aItem.length : Infinity;
    const bLength = Array.isArray(bItem) ? bItem.length : Infinity;
    return aLength - bLength;
  }
}

export function isValidEmail(email?: string): boolean {
  if (!email?.trim()) return true;

  const result = z
    .string()
    .min(1, { message: "This field has to be filled." })
    .email("This is not a valid email.")
    .safeParse(email);

  return result.success;
}

export function findById(id: string) {
  return function findByIdHelper<T extends { id: string }>(items: T[]) {
    return items.find(item => item.id === id);
  }
}

export function findByKey(key: string, value: any) {
  return function findByKeyHelper<T>(items: T[]): any {
    return items.find((item: any) => item[key] === value);
  }
}

export type DiffType = 'new' | 'removed' | 'changed';

export interface DiffFlags {
  new?: boolean;
  removed?: boolean;
  changed?: boolean;
  ignoreColumns?: string[]
}

export function findFilteredDifferences(
  newest: Record<string, any>,
  oldest: Record<string, any>,
  flags: DiffFlags
): string[] {
  const diffKeys: Array<{ key: string; type: DiffType }> = [];
  const ignoreColumns = flags.ignoreColumns || [];

  function compareObjects(obj1: any, obj2: any, prefix = '') {
    // Check properties in obj1
    for (const key in obj1) {
      if (obj1.hasOwnProperty(key)) {
        const fullPath = prefix ? `${prefix}.${key}` : key;

        // Skip ignored columns
        if (ignoreColumns.includes(fullPath)) {
          continue;
        }

        if (obj2 === null) {
          // New property
          diffKeys.push({ key: fullPath, type: 'new' });
        } else if (!obj2.hasOwnProperty(key)) {
          // New property
          diffKeys.push({ key: fullPath, type: 'new' });
        } else if (obj1[key] instanceof Decimal) {
          if (obj2[key] === null) {
            // Changed property
            diffKeys.push({ key: fullPath, type: 'new' });
          } else if (obj1[key] !== obj2[key]) {
            // Changed property
            diffKeys.push({ key: fullPath, type: 'changed' });
          }

        } else if (typeof obj1[key] === 'object' && obj1[key] !== null && !Array.isArray(obj1[key])) {
          // Recursively compare nested objects
          compareObjects(obj1[key], obj2[key], fullPath);
        } else if (obj1[key] !== obj2[key]) {
          // Changed property
          diffKeys.push({ key: fullPath, type: 'changed' });
        }
      }
    }

    // Check for removed properties
    for (const key in obj2) {
      if (
        (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) || 
        (obj2.hasOwnProperty(key) && obj1.hasOwnProperty(key) && !!obj2[key] && obj1[key] == null) 
      ) {
        const fullPath = prefix ? `${prefix}.${key}` : key;

        // Skip ignored columns
        if (ignoreColumns.includes(fullPath)) {
          continue;
        }

        diffKeys.push({ key: fullPath, type: 'removed' });
      }
    }
  }

  compareObjects(newest, oldest);

  // Filter the differences based on the provided flags
  return diffKeys
    .filter(diff => flags[diff.type])
    .map(diff => diff.key);
}
export function toSentenceCase(text: string): string {
  if (!text) return '';

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

type CleanOptions = {
  removeEmptyStrings?: boolean;
  removeFalseBooleans?: boolean;
  removeNegativeNumbers?: boolean;
  removeZeros?: boolean;
  removeAllFalsy?: boolean;
};

export function cleanObject<T extends Record<string, any>>(
  obj: T,
  options: CleanOptions = {}
): Partial<T> {
  const {
    removeEmptyStrings = false,
    removeFalseBooleans = false,
    removeNegativeNumbers = false,
    removeZeros = false,
    removeAllFalsy = false,
  } = options;

  const shouldRemove = (value: any): boolean => {
    // Always remove null and undefined
    if (value === null || value === undefined) {
      return true;
    }

    // If removeAllFalsy is true, remove all falsy values
    if (removeAllFalsy && !value) {
      return true;
    }

    // Check specific conditions
    if (removeEmptyStrings && value === '') {
      return true;
    }

    if (removeFalseBooleans && value === false) {
      return true;
    }

    if (typeof value === 'number') {
      if (removeZeros && value === 0) {
        return true;
      }
      if (removeNegativeNumbers && value < 0) {
        return true;
      }
    }

    return false;
  };

  const result: Partial<T> = {};

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      if (!shouldRemove(value)) {
        result[key] = value;
      }
    }
  }

  return result;
}

export function zodEmptyStringToUndefined(val?: any): undefined | string {
  if (val === '') return undefined;
  return val;
};

export function chunkArray<T>(chunkSize: number, arrayToBeChunked: T[]): T[][] {
  const chunks: any[] = [];

  for (let i = 0; i < arrayToBeChunked.length; i += chunkSize) {
    const chunk = arrayToBeChunked.slice(i, i + chunkSize);
    chunks.push(chunk);
  }

  return chunks;
}
